import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { css } from '@emotion/core';
import { theme } from '@weave/theme-original';
import { Heading, Text, Table, TableColumnConfig } from '@weave/design-system';
import moment from 'moment';
import {
  SsoStatus,
  SsoExcludedUser,
} from '@weave/schema-gen-ts/dist/schemas/auth-api/v3/auth.pb';
import { statusDot } from '../../location-sidebar-container/location-sidebar.styles';
import { CopyToClipboardButton } from '../../shared/copy-to-clipboard/copy-to-clipboard-button';
import { CenteredSpinningLoader } from '../../shared/centered-spinning-loader';
import {
  useGetSsoDetailsForTenant,
  useGetAuthConfigs,
  useGetSsoExcludedUsers,
} from '../../../apis/sso';
import { WEAVE_APP_URL } from '../../../apis/sso/sso-url-config';

const PREVENT_DIRECT_LOGIN_KEYS = [
  'prevent-direct-login',
  'prevent_direct_login',
  'preventDirectLogin',
];

// Extracts a readable message from an axios error without pulling in an axios dependency here.
const getErrorMessage = (error: unknown): string => {
  const response = (
    error as { response?: { data?: { message?: string } }; message?: string }
  )?.response;
  return response?.data?.message ?? (error as Error)?.message ?? 'Unknown error';
};

const SSO_ADMIN_ROLE_NAMES = ['SSO Admin', 'Super Admin'];
const isSsoAdmin = (user: SsoExcludedUser) =>
  !!user.roleName && SSO_ADMIN_ROLE_NAMES.includes(user.roleName);

const formatAddedAt = (addedAt?: string) =>
  addedAt && moment(addedAt).isValid()
    ? moment(addedAt).format('MMM D, YYYY, h:mm A')
    : '—';

const excludedColumns: TableColumnConfig<SsoExcludedUser>[] = [
  {
    Header: 'User',
    id: 'user',
    accessor: (user) => (
      <>
        {user.firstName} {user.lastName}
        {(user.firstName || user.lastName) && user.username && ' — '}
        {user.username}
      </>
    ),
  },
  {
    Header: 'Status',
    id: 'status',
    accessor: (user) => (
      <span css={isSsoAdmin(user) ? blueChip : exemptChip}>
        {isSsoAdmin(user) ? user.roleName : 'Exempted'}
      </span>
    ),
  },
  {
    Header: 'Added By',
    id: 'addedBy',
    accessor: (user) =>
      user.addedByFirstname
        ? `${user.addedByFirstname} ${user.addedByLastname ?? ''}`.trim()
        : user.addedBy ?? '—',
  },
  {
    Header: 'Date Added',
    id: 'dateAdded',
    accessor: (user) => formatAddedAt(user.addedAt),
  },
];

// The url slug users can visit to route directly to this connection's identity provider.
const getIdentifierUrl = (identifier: string) => `${WEAVE_APP_URL}?ssoTeam=${identifier}`;

const card = css`
  border: 1px solid ${theme.colors.gray300};
  border-radius: ${theme.borderRadius.medium};
  padding: ${theme.spacing(3)};
  margin-bottom: ${theme.spacing(3)};

  &:last-child {
    margin-bottom: 0;
  }
`;

const cardHeader = css`
  margin-bottom: ${theme.spacing(2)};
`;

const cardTitle = css`
  margin: 0;
`;

const cardDescription = css`
  margin: ${theme.spacing(0.5, 0, 0)};
`;

const divider = css`
  border: none;
  border-top: 1px solid ${theme.colors.gray200};
  margin: ${theme.spacing(2, 0)};
`;

const connectionRow = css`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${theme.spacing(2)};
`;

const subheading = css`
  margin: 0 0 ${theme.spacing(0.5)};
`;

const subheadingGroup = css`
  margin-bottom: ${theme.spacing(1.5)};
`;

const identifierRow = css`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${theme.spacing(1.5)};
  padding: ${theme.spacing(1.25, 0)};
  border-bottom: 1px solid ${theme.colors.gray200};

  &:last-child {
    border-bottom: none;
  }
`;

const identifierName = css`
  flex: 0 0 auto;
  margin: 0;
`;

const identifierCopyWrap = css`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing(0.5)};
  min-width: 0;

  button {
    width: 24px;
    height: 24px;
    min-width: 24px;
    padding: 0;
    margin-left: 0 !important;
    display: inline-flex;
    align-items: center;
    justify-content: center;

    svg {
      width: 16px;
      height: 16px;
    }
  }
`;

const identifierUrl = css`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const summaryValue = css`
  display: flex;
  align-items: center;
`;

const emptyState = css`
  padding: ${theme.spacing(1, 0)};
`;

const errorBanner = css`
  margin-bottom: ${theme.spacing(3)};
`;

const smallChip = css`
  display: inline-flex;
  align-items: center;
  padding: ${theme.spacing(0.25, 1)};
  border-radius: ${theme.borderRadius.small};
  font-size: ${theme.fontSize(11)};
  font-weight: 600;
`;

const blueChip = css`
  ${smallChip};
  background-color: rgba(0, 106, 255, 0.1);
  color: ${theme.colors.weaveBlue};
`;

const exemptChip = css`
  ${smallChip};
  background-color: ${theme.colors.gray200};
  color: ${theme.colors.gray600};
`;

const SsoStateIndicator = ({
  connectionStatus,
  enforced,
}: {
  connectionStatus?: SsoStatus;
  enforced?: boolean;
}) => {
  const enabled = connectionStatus === SsoStatus.SSO_ACTIVE;
  const label = !enabled
    ? 'Disabled'
    : enforced
    ? 'Enabled - Enforced'
    : 'Enabled - Not Enforced';

  let statusColor: 'success' | 'error' | 'gray500' = 'success';
  if (!enabled) {
    statusColor = 'error';
  } else if (!enforced) {
    statusColor = 'gray500';
  } else {
    statusColor = 'success';
  }

  return (
    <div css={summaryValue}>
      <div css={statusDot(theme, statusColor)} />
      <Text as="span" size="medium">
        {label}
      </Text>
    </div>
  );
};

type Props = {
  orgName?: string;
  locationId?: string;
};

// OrgSsoTab is a read-only view of the organization's configured SSO information:
// identity provider connection + identifiers (via GetSsoDetailsForTenant), enforcement
// state (via GetAuthConfigs's prevent-direct-login flag), the org's static SSO callback
// URLs, and the users with SSO Admin access or an explicit SSO exemption (via
// GetSsoExcludedUsersResponse). Modeled after the customer-facing Single Sign-On settings
// view. GetSsoDetailsForTenant and GetSsoExcludedUsers resolve the tenant from a
// Location-Id header, so this tab requires any location id belonging to the org (see
// OrgDetailsPage).
export const OrgSsoTab = ({ orgName, locationId }: Props) => {
  const { orgId } = useParams<{ orgId: string }>();
  const {
    data: ssoDetailsData,
    isLoading: isSsoDetailsLoading,
    isError: isSsoDetailsError,
    error: ssoDetailsError,
  } = useGetSsoDetailsForTenant(locationId ?? '');
  const details = ssoDetailsData?.details;

  const { data: authConfigsData } = useGetAuthConfigs(
    details?.tenant ?? '',
    locationId ?? ''
  );
  const enforced = authConfigsData?.configs?.some(
    (config) =>
      config.name &&
      PREVENT_DIRECT_LOGIN_KEYS.includes(config.name) &&
      String(config.value).toLowerCase() === 'true'
  );

  const {
    data: excludedUsersData,
    isFetching: isExcludedUsersFetching,
    isError: isExcludedUsersError,
    error: excludedUsersError,
  } = useGetSsoExcludedUsers(locationId ?? '');
  const excludedUsers = excludedUsersData?.users ?? [];

  const aliases = details?.tenantAliases ?? [];
  const linkedOrgIds = details?.orgIds?.filter((id) => id !== orgId) ?? [];

  if (!locationId) {
    return (
      <Text color="light">
        This organization has no locations, so its SSO tenant can&apos;t be resolved.
      </Text>
    );
  }

  if (isSsoDetailsLoading) {
    return (
      <CenteredSpinningLoader
        css={css`
          min-height: 50vh;
        `}
      />
    );
  }

  const isSsoConfigured =
    !!details?.tenant || !!(details?.connections && details.connections.length > 0);

  return (
    <div>
      {isSsoDetailsError && (
        <Text css={errorBanner} color="error">
          Failed to load SSO details: {getErrorMessage(ssoDetailsError)}
        </Text>
      )}
      {isExcludedUsersError && (
        <Text css={errorBanner} color="error">
          Failed to load SSO admins/exempted users: {getErrorMessage(excludedUsersError)}
        </Text>
      )}

      {!isSsoConfigured && !isSsoDetailsError ? (
        <div css={card}>
          <Text css={emptyState} color="light">
            Single Sign-On is not configured for this organization.
          </Text>
        </div>
      ) : (
        <>
          <div css={card}>
            <div css={{ marginBottom: theme.spacing(2) }}>
              <Heading level={2} css={{ margin: 0 }}>
                Identity Provider Connection
              </Heading>
              <Text
                css={{ margin: theme.spacing(0.5, 0, 0) }}
                color="light"
                size="medium"
              >
                The identity provider configured for single sign-on.
              </Text>
            </div>

            <div css={connectionRow}>
              <div>
                <Text weight="semibold">{orgName ?? '—'}</Text>
                {details?.connections?.[0] &&
                  (details.connections[0] as any).identityProvider && (
                    <Text css={cardDescription} color="light" size="medium">
                      Identity Provider:{' '}
                      {(details.connections[0] as any).identityProvider}
                    </Text>
                  )}
              </div>
              <SsoStateIndicator
                connectionStatus={details?.connectionStatus}
                enforced={enforced}
              />
            </div>

            <hr css={divider} />

            <Heading level={3} css={{ margin: theme.spacing(0, 0, 0.5) }}>
              SSO Identifiers
            </Heading>
            <Text css={{ marginBottom: theme.spacing(1.5) }} color="light" size="medium">
              Users can use these tenant identifiers to route to this connection.
            </Text>
            <div>
              {details?.tenant && (
                <div css={identifierRow}>
                  <Text css={identifierName} weight="semibold" as="span">
                    {details.tenant}
                  </Text>
                  <span css={blueChip}>Primary</span>
                  <div css={identifierCopyWrap}>
                    <CopyToClipboardButton copyText={getIdentifierUrl(details.tenant)}>
                      <Text css={identifierUrl} as="span" color="light" size="medium">
                        {getIdentifierUrl(details.tenant)}
                      </Text>
                    </CopyToClipboardButton>
                  </div>
                </div>
              )}
              {aliases.map((alias) => (
                <div key={alias} css={identifierRow}>
                  <Text css={identifierName} weight="semibold" as="span">
                    {alias}
                  </Text>
                  <div css={identifierCopyWrap}>
                    <CopyToClipboardButton copyText={getIdentifierUrl(alias)}>
                      <Text css={identifierUrl} as="span" color="light" size="medium">
                        {getIdentifierUrl(alias)}
                      </Text>
                    </CopyToClipboardButton>
                  </div>
                </div>
              ))}
              {!details?.tenant && aliases.length === 0 && (
                <Text css={emptyState} color="light">
                  No tenant identifiers configured.
                </Text>
              )}
            </div>

            {linkedOrgIds.length > 0 && (
              <>
                <hr css={divider} />
                <Heading level={3} css={{ margin: theme.spacing(0, 0, 0.5) }}>
                  Additional Organizations
                </Heading>
                <Text
                  css={{ marginBottom: theme.spacing(1.5) }}
                  color="light"
                  size="medium"
                >
                  This SSO tenant is also configured for the following organizations:
                </Text>
                <div>
                  {linkedOrgIds.map((id) => (
                    <div key={id} css={identifierRow}>
                      <Link
                        to={`/org/${id}`}
                        css={css`
                          text-decoration: none;
                          color: ${theme.colors.weaveBlue};
                        `}
                      >
                        <Text as="span" weight="semibold">
                          {id}
                        </Text>
                      </Link>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          <div css={card}>
            <div css={{ marginBottom: theme.spacing(2) }}>
              <Heading level={2} css={{ margin: 0 }}>
                Authentication Policy
              </Heading>
            </div>

            <div css={connectionRow}>
              <div>
                <Text weight="semibold">Enforce Single Sign-On</Text>
                <Text color="light" size="medium">
                  Require all users to sign in through SSO.
                </Text>
              </div>
              <div css={summaryValue}>
                <div css={statusDot(theme, enforced ? 'success' : 'error')} />
                <Text as="span" size="medium">
                  {enforced ? 'Enforced' : 'Not Enforced'}
                </Text>
              </div>
            </div>

            <hr css={divider} />

            <Heading level={3} css={{ margin: theme.spacing(0, 0, 0.5) }}>
              Users Exempt From SSO
            </Heading>
            <Text css={{ marginBottom: theme.spacing(1.5) }} color="light" size="medium">
              Users who can still log in with a password, either as an SSO Admin or an
              explicit exemption.
            </Text>

            {isExcludedUsersFetching ? (
              <CenteredSpinningLoader />
            ) : excludedUsers.length === 0 ? (
              <Text css={emptyState} color="light">
                No users are exempted from SSO for this organization.
              </Text>
            ) : (
              <Table colConfig={excludedColumns} data={excludedUsers} />
            )}
          </div>
        </>
      )}
    </div>
  );
};
